import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getRoleLabel } from '@/lib/utils/utils';
import { useTranslation } from 'react-i18next';

export const FORM_SUBSIDIARY_INVITE_ID = 'form-subsidiary-invite';

function getAllAssignableRoles() {
    return [
        { value: 'admin', label: getRoleLabel('admin'), superAdminOnly: true },
        { value: 'owner', label: getRoleLabel('owner'), ownerOnly: true },
        { value: 'head_department', label: getRoleLabel('head_department') },
        { value: 'employee', label: getRoleLabel('employee') },
    ];
}

const DEFAULT_ROLE = 'employee';

const initialInvite = { firstName: '', lastName: '', email: '', role: DEFAULT_ROLE };

export default function FormSubsidiaryInvite({ onSubmit, hasOwner = false, isSuperAdmin = false }) {
    const { t } = useTranslation();
    const ALL_ASSIGNABLE_ROLES = getAllAssignableRoles();
    const assignableRoles = ALL_ASSIGNABLE_ROLES.filter((r) => {
        if (r.superAdminOnly && !isSuperAdmin) return false;
        if (r.ownerOnly && hasOwner) return false;
        return true;
    });
    const [invites, setInvites] = useState([{ ...initialInvite }]);

    const handleChange = (index, field, value) => {
        setInvites((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    };

    const addInvite = () => setInvites((prev) => [...prev, { ...initialInvite }]);

    const removeInvite = (index) => {
        setInvites((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const cleaned = invites.filter((i) => i.email.trim());
        if (cleaned.length === 0) return;
        onSubmit(cleaned);
    };

    return (
        <form id={FORM_SUBSIDIARY_INVITE_ID} onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5">
            <div className="space-y-5 mb-5">
                {invites.map((row, index) => (
                    <div key={index} className="bg-muted rounded-xl p-4 relative border border-border">
                        {index > 0 && (
                            <Button
                                type="button"
                                variant="plain"
                                color="neutral"
                                size="auto"
                                onClick={() => removeInvite(index)}
                                className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-secondary hover:bg-secondary/80 p-0 z-10"
                            >
                                <X className="h-4 w-4 text-foreground" />
                            </Button>
                        )}
                        <div className={`space-y-2`}>
                            <div className="flex gap-2">
                                <Input
                                    type="text"
                                    placeholder={t('subsidiary:prenom2')}
                                    value={row.firstName}
                                    onChange={(e) => handleChange(index, 'firstName', e.target.value)}
                                    className="flex-1 min-w-0 bg-card"
                                    required
                                />
                                <Input
                                    type="text"
                                    placeholder={t('subsidiary:nom')}
                                    value={row.lastName}
                                    onChange={(e) => handleChange(index, 'lastName', e.target.value)}
                                    className="flex-1 min-w-0 bg-card"
                                    required
                                />
                            </div>
                            <Input
                                type="email"
                                placeholder={t('subsidiary:email2')}
                                value={row.email}
                                onChange={(e) => handleChange(index, 'email', e.target.value)}
                                className="bg-card"
                                required
                            />
                            <Select value={row.role} onValueChange={(value) => handleChange(index, 'role', value)}>
                                <SelectTrigger className="bg-card">
                                    <SelectValue placeholder={t('subsidiary:role2')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {assignableRoles.map((role) => (
                                        <SelectItem key={role.value} value={role.value}>
                                            {role.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                ))}
            </div>

            <Button
                type="button"
                variant="plain"
                color="neutral"
                onClick={addInvite}
                className="flex items-center gap-2 mb-6 hover:opacity-70 hover:bg-transparent p-0 h-auto"
            >
                <div className="bg-foreground-dark rounded-md h-6 w-6 flex items-center justify-center">
                    <Plus className="h-4 w-4 text-primary-foreground" strokeWidth={2.5} />
                </div>
                <span className="text-[14px] text-foreground">{t('subsidiary:ajouterUnCollaborateur')}</span>
            </Button>
        </form>
    );
}
